import { msalConfig } from './authConfig';
import { PublicClientApplication } from '@azure/msal-browser';

import { cleanOAuthParamsFromUrl } from '@frontend/features/auth/util/util.auth.cleanOAuthUrl';
import {
  isCodeAlreadyRedeemedError,
  isExpiredAuthCodeError,
} from '@frontend/features/auth/util/util.auth.isAADErrorRecoverable';
import { isLocalhost } from '@frontend/features/auth/util/util.auth.isLocalhost';
import {
  clearRedirectHandled,
  isRedirectHandled,
  markRedirectAsHandled,
} from '@frontend/features/auth/util/util.auth.redirectIdempotency';

// Initialize MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL and handle setup
let initializationPromise: Promise<void> | null = null;

async function initializeMsal(): Promise<void> {
  try {
    // Initialize MSAL instance first
    await msalInstance.initialize();

    // Check if we're on the login page (not callback) and have no accounts
    // This indicates a fresh login attempt, so clear any stale redirect flags
    const isLoginPage = !window.location.pathname.startsWith('/auth/callback');
    const hasAccounts = msalInstance.getAllAccounts().length > 0;

    if (isLoginPage && !hasAccounts && isRedirectHandled()) {
      clearRedirectHandled();
    }

    // Idempotency guard: Prevents double redemption during React StrictMode
    if (isRedirectHandled()) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0 && accounts[0]) {
        msalInstance.setActiveAccount(accounts[0]);
      }

      return;
    }

    // Handle redirect promises after initialization
    // MSAL will check if there's a redirect to handle and return null if not
    try {
      const response = await msalInstance.handleRedirectPromise();

      // Mark redirect as handled after successful processing
      if (response !== null) {
        markRedirectAsHandled();
        // Clean OAuth params from URL to prevent re-processing on refresh
        cleanOAuthParamsFromUrl();
      }
    } catch (redirectError) {
      // AADSTS70008: Authorization code expired - requires fresh login
      if (isExpiredAuthCodeError(redirectError)) {
        clearRedirectHandled();
        msalInstance.setActiveAccount(null);

        // Clear browser storage to remove expired tokens
        try {
          localStorage.clear();
          sessionStorage.clear();
        } catch (storageError) {
          console.warn('[MSAL] Failed to clear storage:', storageError);
        }

        throw redirectError;
      }

      // AADSTS54005: Code already redeemed (common with React StrictMode)
      // Check if user is actually authenticated despite error
      if (isCodeAlreadyRedeemedError(redirectError)) {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          markRedirectAsHandled();
          msalInstance.setActiveAccount(accounts[0] ?? null);
          cleanOAuthParamsFromUrl();
          return;
        }
      }

      // Other errors: rethrow to show error UI
      throw redirectError;
    }

    // Check if there are cached accounts and set active account
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0 && accounts[0]) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  } catch (err) {
    console.error('Error during MSAL initialization:', err);
    throw err;
  }
}

// Create singleton initialization promise
export function ensureMsalInitialized(): Promise<void> {
  if (!initializationPromise) {
    initializationPromise = initializeMsal();
  }
  return initializationPromise;
}

// Auto-initialize MSAL (will only be used on deployed environments)
// Localhost always uses token exchange mode via AuthProvider
if (!isLocalhost()) {
  ensureMsalInitialized().catch((err) => {
    console.error('Failed to initialize MSAL:', err);
  });
} else {
  console.log('[AUTH] Localhost detected - using token exchange mode');
}
